<div class="ds-grid ds-grid--2">
	<!-- 1. Basic view switcher -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Basic (View Switcher)</h3>
			<p class="ds-example__header-subtitle">Controlled via [value] and (valueChange); no form needed</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-segmented-control
				[options]="periodOptions"
				[value]="periodValue"
				(valueChange)="periodValue = $event">
			</eif-custom-segmented-control>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Value:</div>
				<div class="ds-value__content">{{ periodValue }}</div>
			</div>
		</div>
	</div>

	<!-- 2. With icons -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">With Icons</h3>
			<p class="ds-example__header-subtitle">Each option can display a Font Awesome or SVG icon</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-segmented-control
				[options]="viewOptions"
				[value]="viewValue"
				(valueChange)="viewValue = $event">
			</eif-custom-segmented-control>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Value:</div>
				<div class="ds-value__content">{{ viewValue }}</div>
			</div>
		</div>
	</div>

	<!-- 3. Icon only -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Icon Only</h3>
			<p class="ds-example__header-subtitle">[iconOnly] forces the icon presentation; labels become tooltips</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-segmented-control
				[options]="alignOptions"
				[iconOnly]="true"
				[value]="alignValue"
				(valueChange)="alignValue = $event">
			</eif-custom-segmented-control>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Value:</div>
				<div class="ds-value__content">{{ alignValue }}</div>
			</div>
		</div>
	</div>

	<!-- 4. Numeric values -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Numeric Values</h3>
			<p class="ds-example__header-subtitle">Option values can be numbers, bound via [(value)]</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-segmented-control
				[options]="pageSizeOptions"
				[(value)]="pageSizeValue">
			</eif-custom-segmented-control>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Page size:</div>
				<div class="ds-value__content">{{ pageSizeValue }}</div>
			</div>
		</div>
	</div>

	<!-- 5. Colors -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Colors</h3>
			<p class="ds-example__header-subtitle">One instance per available color</p>
		</div>
		<div class="ds-example__content">
			<div class="ds-group ds-group--vertical">
				@for (color of colors; track color) {
					<div class="segmented-color-row">
						<span class="segmented-color-row__name">{{ color }}</span>
						<eif-custom-segmented-control
							[options]="genericOptions"
							[color]="color"
							[value]="colorValue"
							(valueChange)="colorValue = $event">
						</eif-custom-segmented-control>
					</div>
				}
			</div>
		</div>
	</div>

	<!-- 6. Sizes -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Sizes</h3>
			<p class="ds-example__header-subtitle">Small, medium and large</p>
		</div>
		<div class="ds-example__content">
			<div class="ds-group ds-group--vertical">
				@for (size of sizes; track size) {
					<div class="segmented-color-row">
						<span class="segmented-color-row__name">{{ size }}</span>
						<eif-custom-segmented-control
							[options]="genericOptions"
							[size]="size"
							[value]="sizeValue"
							(valueChange)="sizeValue = $event">
						</eif-custom-segmented-control>
					</div>
				}
			</div>
		</div>
	</div>

	<!-- 6b. Variants -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Variants</h3>
			<p class="ds-example__header-subtitle">Standard (filled) and outlined</p>
		</div>
		<div class="ds-example__content">
			<div class="ds-group ds-group--vertical">
				@for (variant of variants; track variant) {
					<div class="segmented-color-row">
						<span class="segmented-color-row__name">{{ variant }}</span>
						<eif-custom-segmented-control
							[options]="viewOptions"
							[variant]="variant"
							[value]="variantValue"
							(valueChange)="variantValue = $event">
						</eif-custom-segmented-control>
					</div>
				}
			</div>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Value:</div>
				<div class="ds-value__content">{{ variantValue }}</div>
			</div>
		</div>
	</div>

	<!-- 6c. Outlined Colors -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Outlined Colors</h3>
			<p class="ds-example__header-subtitle">Outlined variant in every available color</p>
		</div>
		<div class="ds-example__content">
			<div class="ds-group ds-group--vertical">
				@for (color of colors; track color) {
					<div class="segmented-color-row">
						<span class="segmented-color-row__name">{{ color }}</span>
						<eif-custom-segmented-control
							[options]="genericOptions"
							[color]="color"
							[variant]="'outlined'"
							[value]="outlinedColorValue"
							(valueChange)="outlinedColorValue = $event">
						</eif-custom-segmented-control>
					</div>
				}
			</div>
		</div>
	</div>

	<!-- 7. States -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">States</h3>
			<p class="ds-example__header-subtitle">Disabled option, disabled control and conditional options</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-segmented-control
				[options]="statesOptions"
				[value]="'active'">
			</eif-custom-segmented-control>
			<eif-custom-segmented-control
				[options]="disabledOptions"
				[disabled]="true"
				[value]="'x'">
			</eif-custom-segmented-control>
			<div class="ds-group">
				<eif-custom-button
					[text]="showArchived ? 'Hide archived' : 'Show archived'"
					[variant]="'outlined'"
					[size]="'small'"
					(action)="toggleArchived()">
				</eif-custom-button>
			</div>
			<eif-custom-segmented-control
				[options]="conditionalOptions"
				[value]="statesValue"
				(valueChange)="statesValue = $event">
			</eif-custom-segmented-control>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Value:</div>
				<div class="ds-value__content">{{ statesValue }}</div>
			</div>
		</div>
	</div>

	<!-- 8. Form integration (template-driven) -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Form Integration</h3>
			<p class="ds-example__header-subtitle">Template-driven ngModel with label and required validation</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-segmented-control
				#planControl
				label="Plan"
				name="plan"
				[options]="periodOptions"
				[validationRules]="requiredPlanRules"
				[(ngModel)]="formValue"
				(validationChange)="onValidationChange($event)">
			</eif-custom-segmented-control>
			<eif-custom-button
				[text]="'Validate'"
				[type]="'button'"
				[size]="'small'"
				[preIcon]="'fa-solid fa-check'"
				(action)="planControl.validate()">
			</eif-custom-button>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Value / Valid:</div>
				<div class="ds-value__content">{{ formValue ?? 'null' }} / {{ formValid ? 'Yes' : 'No' }}</div>
			</div>
		</div>
	</div>

	<!-- 9. Reactive forms -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Reactive Forms</h3>
			<p class="ds-example__header-subtitle">Bound to a typed FormControl; disable() toggles setDisabledState</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-segmented-control
				[options]="periodOptions"
				[formControl]="reactiveControl">
			</eif-custom-segmented-control>
			<eif-custom-button
				[text]="reactiveControl.disabled ? 'Enable control' : 'Disable control'"
				[variant]="'outlined'"
				[size]="'small'"
				(action)="toggleReactiveDisabled()">
			</eif-custom-button>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">control.value:</div>
				<div class="ds-value__content">{{ reactiveControl.value }}</div>
			</div>
		</div>
	</div>

	<!-- 10. Readonly -->
	<div class="ds-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Readonly</h3>
			<p class="ds-example__header-subtitle">Readonly display of the selected option</p>
		</div>
		<div class="ds-example__content">
			<eif-custom-segmented-control
				label="Status"
				[options]="playgroundTextOptions"
				[readonlyValue]="true"
				[value]="readonlyValue">
			</eif-custom-segmented-control>
		</div>
	</div>

	<!-- 11. Responsive playground -->
	<div class="ds-example segmented-playground-example">
		<div class="ds-example__header">
			<h3 class="ds-example__header-title">Responsive Playground</h3>
			<p class="ds-example__header-subtitle">
				The layout adapts to the available width: natural → compact (ellipsized labels) → icon-only → scroll with arrows. Use the presets or
				drag the container's right edge.
			</p>
		</div>
		<div class="ds-example__content">
			<div class="ds-group">
				@for (preset of playgroundPresets; track preset.id) {
					<eif-custom-button
						[text]="preset.label"
						[variant]="playgroundWidth === preset.id ? 'standard' : 'outlined'"
						[size]="'small'"
						(action)="setPlaygroundWidth(preset.id)">
					</eif-custom-button>
				}
			</div>
			<div
				class="segmented-playground"
				[class]="playgroundClass">
				<div class="segmented-playground__item">
					<p class="segmented-playground__caption">Text only → compact → scroll</p>
					<eif-custom-segmented-control
						[options]="playgroundTextOptions"
						[value]="playgroundTextValue"
						(valueChange)="playgroundTextValue = $event">
					</eif-custom-segmented-control>
				</div>
				<div class="segmented-playground__item">
					<p class="segmented-playground__caption">With icons → compact → icon-only → scroll</p>
					<eif-custom-segmented-control
						[options]="playgroundIconOptions"
						[value]="playgroundIconValue"
						(valueChange)="playgroundIconValue = $event">
					</eif-custom-segmented-control>
				</div>
				<div class="segmented-playground__item">
					<p class="segmented-playground__caption">Full width with icons</p>
					<eif-custom-segmented-control
						[options]="playgroundIconOptions"
						[fullWidth]="true"
						[value]="playgroundFullWidthValue"
						(valueChange)="playgroundFullWidthValue = $event">
					</eif-custom-segmented-control>
				</div>
				<div class="segmented-playground__item">
					<p class="segmented-playground__caption">Inside a wrapping flex toolbar next to a heading</p>
					<div class="d-flex justify-content-between align-items-center gap-3 flex-wrap">
						<strong>Documents</strong>
						<eif-custom-segmented-control
							[options]="viewOptions"
							[value]="playgroundToolbarValue"
							(valueChange)="playgroundToolbarValue = $event">
						</eif-custom-segmented-control>
					</div>
				</div>
			</div>
		</div>
		<div class="ds-example__footer">
			<div class="ds-value">
				<div class="ds-value__label">Container width:</div>
				<div class="ds-value__content">{{ playgroundWidthLabel }}</div>
			</div>
		</div>
	</div>
</div>
